import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import Logo from "../../assets/icons/ustaz-kids.png";
import useAuth from "../../hooks/useAuth";
import SimpleLoader from "../../components/Loader/SimpleLoader";

function parseAuthError(err) {
  const data = err?.response?.data ?? err?.data ?? err;

  // String errors from server
  if (typeof data === "string") return data;

  // Common DRF/REST shapes
  if (data?.non_field_errors?.length) return data.non_field_errors[0];
  if (data?.detail) return data.detail;
  if (data?.message) return data.message;
  if (data?.error) return data.error;

  // Fall back to field errors (return first we find)
  if (data?.username?.length) return data.username[0];
  if (data?.email?.length) return data.email[0];
  if (data?.password?.length) return data.password[0];

  // Last resort
  return err?.message || "Қате орын алды. Қайтадан көріңіз.";
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { loginUser, error: authError } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null); // global error for non_field_errors/etc.

  const {
    register,
    handleSubmit,
    setError, // ✅ for field-level server errors
    clearErrors, // ✅ to clear on change
    formState: { errors },
    watch,
  } = useForm();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  // Clear global/field errors when user edits either field
  const watchedUsername = watch("username");
  const watchedPassword = watch("password");
  useMemo(() => {
    if (submitError) setSubmitError(null);
    if (errors.username || errors.password) {
      // Clear only server-level errors; keep client-side rules
      clearErrors(["username", "password"]);
    }
  }, [watchedUsername, watchedPassword]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (data) => {
    setSubmitError(null);
    const payload = {
      username: (data.username || "").trim(),
      password: data.password || "",
    };

    try {
      setLoading(true);
      await loginUser(payload);
      // success path handled by the hook (e.g., navigating, storing tokens)
    } catch (err) {
      // Parse server response & route to field/global
      const server = err?.response?.data ?? err?.data ?? {};
      // Attach field-level messages if backend sent them
      if (server?.username?.length) {
        setError("username", { type: "server", message: server.username[0] });
      }
      if (server?.password?.length) {
        setError("password", { type: "server", message: server.password[0] });
      }
      // Global error (non_field_errors, detail, etc.)
      setSubmitError(parseAuthError(err));
      console.error("Login failed:", err);
    } finally {
      setLoading(false);
    }
  };

  // Prefer local submitError (from this attempt), else show error from useAuth
  const globalError =
    submitError || (authError ? parseAuthError(authError) : null);

  return (
    <div className="p-4 flex-grow flex items-center justify-center bg-gray-100">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-8">
        <h1 className="text-3xl text-center text-[#674da2] font-semibold mb-8">
          Кіру
        </h1>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* Пайдаланушы аты */}
          <div>
            <label className="block mb-1 text-gray-600">Пайдаланушы аты</label>
            <input
              {...register("username", {
                required: "Пайдаланушы атын енгізу міндетті",
              })}
              type="text"
              placeholder=""
              className={`w-full pl-3 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.username
                  ? "border-red-400 focus:ring-red-400"
                  : "border-gray-300 focus:ring-[#674da2]"
              }`}
              aria-invalid={!!errors.username}
            />
            {errors.username && (
              <p className="text-sm text-red-500 mt-1">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Құпиясөз */}
          <div>
            <label className="block mb-1 text-gray-600">Құпиясөз</label>
            <div className="relative">
              <input
                {...register("password", {
                  required: "Құпиясөз енгізу міндетті",
                  minLength: {
                    value: 6,
                    message: "Құпиясөз кемінде 6 таңбадан тұруы керек",
                  },
                })}
                type={showPassword ? "text" : "password"}
                className={`w-full pl-3 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.password
                    ? "border-red-400 focus:ring-red-400"
                    : "border-gray-300 focus:ring-[#674da2]"
                }`}
                aria-invalid={!!errors.password}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500 mt-1">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Server/global error */}
          {globalError && (
            <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-2 text-sm text-center">
              {globalError}
            </div>
          )}

          {/* Кіру */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 rounded-lg transition flex justify-center items-center ${
              loading
                ? "bg-[#a596c7] cursor-not-allowed"
                : "bg-[#674da2] hover:bg-[#7052b7]"
            } text-white`}
          >
            {loading ? <SimpleLoader className="w-6 h-6 text-white" /> : "Кіру"}
          </button>
        </form>

        <div className="mt-10 flex justify-center">
          <img src={Logo} alt="Ustaz Kids" className="h-10" />
        </div>
      </div>
    </div>
  );
}
